import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { firestore } from '../../firebase/config';
import './ServiceManager.css';

const ServiceManager = () => {
  const { userProfile } = useContext(AuthContext);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareServiceId, setShareServiceId] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // État du formulaire - ajout des champs pour les services à domicile
  const [formData, setFormData] = useState({
    title: '',
    duration: 60,
    price: 50,
    description: '',
    active: true,
    restTime: 0,
    notes: '',
    dailyLimit: 0,
    canBeInCabinet: true, // Nouveau : peut être dispensé en cabinet
    canBeRemote: false,
    remotePrice: 0,
    canBeAtHome: false, // Nouveau : peut être dispensé à domicile
    homePrice: 0, // Nouveau : prix pour service à domicile
    homeRadius: 10, // Nouveau : rayon d'intervention en km
    homeTransportFee: 0, // Nouveau : frais de déplacement
  });

  // Charger les services du praticien
  useEffect(() => {
    const fetchServices = async () => {
      if (!userProfile?.practitionerId) return;

      try {
        const snapshot = await firestore
          .collection('services')
          .where('practitionerId', '==', userProfile.practitionerId)
          .get();

        const servicesList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setServices(servicesList);
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des services:', error);
        setLoading(false);
      }
    };

    fetchServices();
  }, [userProfile]);

  // Gérer la sélection d'un service pour édition
  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setFormData({
      title: service.title,
      duration: service.duration,
      price: service.price,
      description: service.description || '',
      active: service.active,
      restTime: service.restTime || 0,
      notes: service.notes || '',
      dailyLimit: service.dailyLimit || 0,
      canBeInCabinet: service.canBeInCabinet || true,
      canBeRemote: service.canBeRemote || false,
      remotePrice: service.remotePrice || service.price,
      canBeAtHome: service.canBeAtHome || false,
      homePrice: service.homePrice || service.price,
      homeRadius: service.homeRadius || 10,
      homeTransportFee: service.homeTransportFee || 0,
    });
    setIsAddingNew(false);
  };

  // Gérer l'ajout d'un nouveau service
  const handleAddNew = () => {
    setSelectedService(null);
    setFormData({
      title: '',
      duration: 60,
      price: 50,
      description: '',
      active: true,
      restTime: 0,
      notes: '',
      dailyLimit: 0,
      canBeInCabinet: true, // Par défaut, peut être dispensé en cabinet
      canBeRemote: false,
      remotePrice: 0,
      canBeAtHome: false,
      homePrice: 0,
      homeRadius: 10,
      homeTransportFee: 0,
    });
    setIsAddingNew(true);
  };

  // Mettre à jour les champs du formulaire avec logique pour les services exclusifs
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    let newFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked :
        (type === 'number' ? parseInt(value, 10) : value)
    };

    // Validation : au moins une option doit être cochée
    if (type === 'checkbox' && (name === 'canBeInCabinet' || name === 'canBeRemote' || name === 'canBeAtHome')) {
      const { canBeInCabinet, canBeRemote, canBeAtHome } = newFormData;

      // Si l'utilisateur essaie de décocher la dernière option activée, empêcher l'action
      if (!canBeInCabinet && !canBeRemote && !canBeAtHome) {
        alert('Au moins une option de consultation doit être activée.');
        return; // Ne pas mettre à jour le state si aucune option n'est cochée
      }
    }

    setFormData(newFormData);
  };

  // Sauvegarder le service avec les nouveaux champs
  const handleSaveService = async (e) => {
    e.preventDefault();

    if (!userProfile?.practitionerId) return;

    try {
      const serviceData = {
        practitionerId: userProfile.practitionerId,
        title: formData.title,
        duration: formData.duration,
        price: formData.price,
        description: formData.description,
        active: formData.active,
        restTime: formData.restTime,
        notes: formData.notes,
        dailyLimit: formData.dailyLimit,
        canBeInCabinet: formData.canBeInCabinet, // NOUVEAU
        canBeRemote: formData.canBeRemote,
        remotePrice: formData.canBeRemote ? formData.remotePrice : 0,
        canBeAtHome: formData.canBeAtHome,
        homePrice: formData.canBeAtHome ? formData.homePrice : 0,
        homeRadius: formData.canBeAtHome ? formData.homeRadius : 0,
        homeTransportFee: formData.canBeAtHome ? formData.homeTransportFee : 0,
      };

      if (isAddingNew) {
        const docRef = await firestore
          .collection('services')
          .add(serviceData);

        setServices([
          ...services,
          { id: docRef.id, ...serviceData }
        ]);
      } else if (selectedService) {
        await firestore
          .collection('services')
          .doc(selectedService.id)
          .update(serviceData);

        setServices(services.map(service => {
          if (service.id === selectedService.id) {
            return { id: service.id, ...serviceData };
          }
          return service;
        }));
      }

      setIsAddingNew(false);
      setSelectedService(null);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du service:', error);
    }
  };

  // Fonctions utilitaires (identiques à l'original)
  const handleCancelEdit = () => {
    setIsAddingNew(false);
    setSelectedService(null);
  };

  const handleDeleteService = async () => {
    if (!selectedService) return;

    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce service ?')) {
      return;
    }

    try {
      await firestore
        .collection('services')
        .doc(selectedService.id)
        .delete();

      setServices(services.filter(service => service.id !== selectedService.id));
      setSelectedService(null);
    } catch (error) {
      console.error('Erreur lors de la suppression du service:', error);
    }
  };

  const handleShareService = (event, service) => {
    event.stopPropagation();
    setShareServiceId(service.id);
    setShowShareModal(true);
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
    setShareServiceId(null);
    setCopySuccess(false);
  };

  const generateBookingLink = (serviceId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/booking/${userProfile.practitionerId}/${serviceId}`;
  };

  const copyToClipboard = () => {
    if (!shareServiceId) return;

    const linkToCopy = generateBookingLink(shareServiceId);

    navigator.clipboard.writeText(linkToCopy)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      })
      .catch(err => {
        console.error('Erreur lors de la copie:', err);
        alert('Échec de la copie');
      });
  };

  // Fonction pour déterminer le type de service pour l'affichage
  const getServiceTypeDisplay = (service) => {

    const types = [];
    if (service.canBeInCabinet !== false) types.push('En cabinet');
    if (service.canBeRemote) types.push('À distance');
    if (service.canBeAtHome) types.push('À domicile');

    return types.length > 0 ? types.join(' & ') : 'Aucune option activée';
  };

  if (loading) {
    return <div className="loading">Chargement des services...</div>;
  }

  const serviceToShare = services.find(service => service.id === shareServiceId);

  return (
    <div className="service-manager">
      <div className="service-list">
        <div className="service-list-header">
          <h3>Vos services</h3>
          <button className="btn btn-primary" onClick={handleAddNew}>
            Ajouter un service
          </button>
        </div>

        {services.length === 0 ? (
          <div className="no-services">
            <p>Vous n'avez pas encore ajouté de services.</p>
            <button className="btn btn-primary" onClick={handleAddNew}>
              Ajouter votre premier service
            </button>
          </div>
        ) : (
          <div className="service-cards">
            {services.map(service => (
              <div
                key={service.id}
                className={`service-card ${selectedService?.id === service.id ? 'selected' : ''} ${!service.active ? 'inactive' : ''}`}
                onClick={() => handleServiceSelect(service)}
              >
                <div className="service-card-header">
                  <h4>{service.title}</h4>
                  <span className={`status ${service.active ? 'active' : 'inactive'}`}>
                    {service.active ? 'Actif' : 'Inactif'}
                  </span>
                </div>
                <div className="service-details">
                  <div className="service-detail">
                    <span className="detail-label">Durée:</span>
                    <span className="detail-value">{service.duration} min</span>
                  </div>
                  <div className="service-detail">
                    <span className="detail-label">Prix:</span>
                    <span className="detail-value">{service.price}€</span>
                  </div>
                  {(service.restTime > 0) && (
                    <div className="service-detail">
                      <span className="detail-label">Repos:</span>
                      <span className="detail-value">{service.restTime} min</span>
                    </div>
                  )}
                  {(service.dailyLimit > 0) && (
                    <div className="service-detail">
                      <span className="detail-label">Limite/jour:</span>
                      <span className="detail-value">{service.dailyLimit}</span>
                    </div>
                  )}
                </div>

                {/* Badge pour le type de service */}
                <div className="service-type-badge">
                  {getServiceTypeDisplay(service)}
                </div>

                {/* Informations spécifiques aux services à domicile */}
                {service.canBeAtHome && (
                  <div className="home-service-info">
                    <div className="home-detail">
                      <span className="detail-label">Rayon:</span>
                      <span className="detail-value">{service.homeRadius} km</span>
                    </div>
                    {service.homeTransportFee > 0 && (
                      <div className="home-detail">
                        <span className="detail-label">Frais déplacement:</span>
                        <span className="detail-value">{service.homeTransportFee}€</span>
                      </div>
                    )}
                  </div>
                )}

                {service.description && (
                  <div className="service-description">
                    {service.description}
                  </div>
                )}

                <div className="service-card-actions">
                  <button
                    className="btn btn-share"
                    onClick={(e) => handleShareService(e, service)}
                    title="Partager le lien direct vers ce service"
                  >
                    <i className="fas fa-share-alt"></i> Partager
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {(isAddingNew || selectedService) && (
        <div className="service-form">
          <h3>{isAddingNew ? 'Ajouter un nouveau service' : 'Modifier le service'}</h3>

          <form onSubmit={handleSaveService}>
            <div className="form-group">
              <label htmlFor="title">Titre du service</label>
              <input
                type="text"
                id="title"
                name="title"
                className="form-control"
                value={formData.title}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="duration">Durée (minutes)</label>
                <input
                  type="number"
                  id="duration"
                  name="duration"
                  className="form-control"
                  value={formData.duration}
                  onChange={handleInputChange}
                  min="15"
                  max="240"
                  step="5"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="price">Prix en cabinet (€)</label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  className="form-control"
                  value={formData.price}
                  onChange={handleInputChange}
                  min="0"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="restTime">Temps de repos après service (minutes)</label>
                <input
                  type="number"
                  id="restTime"
                  name="restTime"
                  className="form-control"
                  value={formData.restTime}
                  onChange={handleInputChange}
                  min="0"
                  max="120"
                  step="5"
                />
                <div className="text-muted">
                  Définit une période de repos après chaque rendez-vous.
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="dailyLimit">Limite de rendez-vous par jour</label>
                <input
                  type="number"
                  id="dailyLimit"
                  name="dailyLimit"
                  className="form-control"
                  value={formData.dailyLimit}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="1"
                />
                <div className="text-muted">
                  0 = pas de limite
                </div>
              </div>
            </div>

            {/* Section pour les options de consultation en cabinet */}
            <div className="form-section">
              <h4>Options de consultation en cabinet</h4>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="canBeInCabinet"
                    checked={formData.canBeInCabinet}
                    onChange={handleInputChange}
                  />
                  Ce service peut être dispensé en cabinet
                </label>
              </div>

              {/* Le prix en cabinet est le prix de base, pas besoin de champ séparé */}
              {formData.canBeInCabinet && (
                <div className="text-muted">
                  Le prix en cabinet correspond au prix de base du service.
                </div>
              )}
            </div>

            {/* MODIFIER la section existante Remote pour garder la logique actuelle */}
            <div className="form-section">
              <h4>Options de consultation à distance</h4>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="canBeRemote"
                    checked={formData.canBeRemote}
                    onChange={handleInputChange}
                  />
                  Ce service peut être dispensé à distance
                </label>
              </div>

              {formData.canBeRemote && (
                <>
                  <div className="form-group">
                    <label htmlFor="remotePrice">Prix pour service à distance (€)</label>
                    <input
                      type="number"
                      id="remotePrice"
                      name="remotePrice"
                      className="form-control"
                      value={formData.remotePrice}
                      onChange={handleInputChange}
                      min="0"
                    />
                  </div>
                </>
              )}
            </div>

            {/* MODIFIER la section existante Home pour garder la logique actuelle */}
            <div className="form-section">
              <h4>Options de consultation à domicile</h4>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="canBeAtHome"
                    checked={formData.canBeAtHome}
                    onChange={handleInputChange}
                  />
                  Ce service peut être dispensé à domicile
                </label>
              </div>

              {formData.canBeAtHome && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="homePrice">Prix pour service à domicile (€)</label>
                      <input
                        type="number"
                        id="homePrice"
                        name="homePrice"
                        className="form-control"
                        value={formData.homePrice}
                        onChange={handleInputChange}
                        min="0"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="homeRadius">Rayon d'intervention (km)</label>
                      <input
                        type="number"
                        id="homeRadius"
                        name="homeRadius"
                        className="form-control"
                        value={formData.homeRadius}
                        onChange={handleInputChange}
                        min="1"
                        max="100"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="homeTransportFee">Frais de déplacement (€)</label>
                    <input
                      type="number"
                      id="homeTransportFee"
                      name="homeTransportFee"
                      className="form-control"
                      value={formData.homeTransportFee}
                      onChange={handleInputChange}
                      min="0"
                      step="0.5"
                    />
                    <div className="text-muted">
                      Frais fixes ajoutés au prix du service pour les déplacements.
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Ajouter un message d'aide en bas des sections de consultation */}
            <div className="consultation-options-help">
              <div className="text-muted">
                <strong>Note :</strong> Au moins une option de consultation doit être activée.
                Le cabinet est coché par défaut mais peut être désactivé si vous proposez uniquement
                des consultations à distance ou à domicile.
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                className="form-control"
                value={formData.description}
                onChange={handleInputChange}
                maxLength={300}
                rows="4"
              ></textarea>
              <div className="character-counter">
                {formData.description.length}/300 caractères
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notes spécifiques au service</label>
              <textarea
                id="notes"
                name="notes"
                className="form-control"
                value={formData.notes}
                onChange={handleInputChange}
                rows="3"
              ></textarea>
              <div className="text-muted">
                Ces notes seront incluses dans les emails de confirmation.
              </div>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleInputChange}
                />
                Service actif
              </label>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-cancel" onClick={handleCancelEdit}>
                Annuler
              </button>

              {!isAddingNew && (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteService}
                >
                  Supprimer
                </button>
              )}

              <button type="submit" className="btn btn-primary">
                {isAddingNew ? 'Ajouter' : 'Enregistrer'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Modal de partage (identique à l'original) */}
      {showShareModal && serviceToShare && (
        <div className="share-modal">
          <div className="share-modal-content">
            <div className="share-modal-header">
              <h3>Partager le lien de réservation</h3>
              <button className="close-modal" onClick={handleCloseShareModal}>
                &times;
              </button>
            </div>

            <p>Partagez ce lien pour permettre à vos clients de réserver directement le service <strong>{serviceToShare.title}</strong> :</p>

            <div className="booking-link-container">
              <input
                type="text"
                value={generateBookingLink(shareServiceId)}
                readOnly
                className="booking-link-input"
              />
              <button
                className="btn btn-copy"
                onClick={copyToClipboard}
              >
                {copySuccess ? 'Copié !' : 'Copier'}
              </button>
            </div>

            <div className="share-options">
              <a
                href={`https://wa.me/?text=Prenez%20rendez-vous%20pour%20${encodeURIComponent(serviceToShare.title)}%20en%20cliquant%20sur%20ce%20lien%20:%20${encodeURIComponent(generateBookingLink(shareServiceId))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-option whatsapp"
              >
                <i className="fab fa-whatsapp"></i>
                WhatsApp
              </a>
              <a
                href={`mailto:?subject=Réservation%20pour%20${encodeURIComponent(serviceToShare.title)}&body=Bonjour,%0A%0AVoici%20le%20lien%20pour%20prendre%20rendez-vous%20pour%20${encodeURIComponent(serviceToShare.title)}%20:%0A%0A${encodeURIComponent(generateBookingLink(shareServiceId))}`}
                className="share-option email"
              >
                <i className="far fa-envelope"></i>
                Email
              </a>
              <a
                href={`sms:?body=Prenez%20rendez-vous%20pour%20${encodeURIComponent(serviceToShare.title)}%20en%20cliquant%20sur%20ce%20lien%20:%20${encodeURIComponent(generateBookingLink(shareServiceId))}`}
                className="share-option sms"
              >
                <i className="fas fa-sms"></i>
                SMS
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceManager;